<script setup lang="ts">
import {
  ref,
  shallowRef,
  onMounted,
  onBeforeUnmount,
  computed,
  watch,
  nextTick,
} from 'vue'
import {
  Eye,
  Edit3,
  Maximize2,
  Minimize2,
  Sun,
  Moon,
  ChevronDown,
  Columns2,
  PanelsLeftRight,
} from 'lucide-vue-next'
import { Dropdown, message } from 'ant-design-vue'
import Toolbar from './Toolbar.vue'
import Preview from './Preview.vue'
import Renderer from './Renderer.vue'
import { createEditor, getEditorTypeName, type EditorType } from '../core/editor-factory'
import type { EditorAdapter, EditorOptions } from '../core/editor-adapter'
import { MarkdownRenderer } from '../core/markdown-renderer'

type EditorMode = 'edit' | 'render'
type LayoutMode = 'split' | 'editor' | 'preview'

const props = withDefaults(
  defineProps<{
    modelValue?: string
    mode?: EditorMode
    editorType?: EditorType
    theme?: 'light' | 'dark'
    height?: string | number
    placeholder?: string
    readOnly?: boolean
    enableKatex?: boolean
    enableTypst?: boolean
    enableShiki?: boolean
    minimap?: boolean
    wordWrap?: boolean
    fontSize?: number
    tabSize?: number
    toolbarGroups?: unknown[]
  }>(),
  {
    modelValue: '',
    mode: 'edit',
    editorType: 'codemirror',
    theme: 'light',
    height: 600,
    placeholder: '开始输入 Markdown...',
    readOnly: false,
    enableKatex: true,
    enableTypst: true,
    enableShiki: true,
    minimap: true,
    wordWrap: true,
    fontSize: 14,
    tabSize: 4
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'change', value: string): void
  (e: 'update:mode', mode: EditorMode): void
  (e: 'mode-change', mode: EditorMode): void
  (e: 'update:editorType', type: EditorType): void
}>()

const containerRef = ref<HTMLElement | null>(null)
const editorContainerRef = ref<HTMLElement | null>(null)
const previewContainerRef = ref<HTMLElement | null>(null)
const rendererContainerRef = ref<HTMLElement | null>(null)

const mode = ref<EditorMode>(props.mode)
const layout = ref<LayoutMode>('split')
const theme = ref<'light' | 'dark'>(props.theme)
const isFullscreen = ref(false)

const editorType = ref<EditorType>(props.editorType)
const editor = shallowRef<EditorAdapter | null>(null)
const markdownRenderer = shallowRef<MarkdownRenderer | null>(null)
let renderer: MarkdownRenderer | null = null

const currentValue = ref<string>(props.modelValue)

const editorTypeLabel = computed(() => getEditorTypeName(editorType.value))

const editorComponentMenu = computed(() => [
  { key: 'monaco', label: 'Monaco Editor' },
  { key: 'codemirror', label: 'CodeMirror 6' },
  { key: 'ace', label: 'Ace Editor' },
])

const wordCount = computed(() => currentValue.value.length)

function switchMode(key: string | number) {
  switchModeType(String(key))
}

function switchModeType(t: string) {
  const valid: EditorType[] = ['monaco', 'codemirror', 'ace']
  if (valid.includes(t as EditorType)) {
    selectEditorType(t as EditorType)
  }
}

function toggleTheme() {
  theme.value = theme.value === 'dark' ? 'light' : 'dark'
  document.documentElement.setAttribute('data-theme', theme.value)
  if (mode.value === 'edit') {
    applyTheme()
  }
}

watch(
  () => props.modelValue,
  (nv) => {
    if (nv !== currentValue.value) {
      currentValue.value = nv
    }
  },
)

watch(
  () => props.editorType,
  async (nt) => {
    editorType.value = nt
    if (mode.value === 'edit') {
      await recreateEditor()
    }
  },
)

watch(
  () => props.mode,
  async (nv) => {
    mode.value = nv
    await nextTick()
  },
)

watch(
  () => props.theme,
  (nv) => {
    theme.value = nv
    if (editor.value && mode.value === 'edit') {
      applyTheme()
    }
  },
)

async function applyTheme() {
  if (!editor.value) return
  // Monaco theme handled via dispose/recreate; CodeMirror via CSS vars
  if (editorType.value === 'monaco') {
    await recreateEditor()
  }
}

onMounted(async () => {
  renderer = new MarkdownRenderer({
    enableKatex: props.enableKatex,
    enableTypst: props.enableTypst,
    enableShiki: props.enableShiki,
    highlightTheme: theme.value === 'dark' ? ['github-dark', 'one-light'] : ['one-light', 'github-dark'],
  })
  markdownRenderer.value = renderer
  await renderer.initialize()
  if (mode.value === 'edit') {
    await initEditor()
  }
})

onBeforeUnmount(() => {
  if (editor.value) editor.value.dispose()
})

async function recreateEditor(target?: EditorType) {
  const t = target ?? editorType.value
  if (editor.value) editor.value.dispose()
  editor.value = null
  editorContainerRef.value!.innerHTML = ''
  await nextTick()
  await initEditor(t)
}

async function initEditor(target?: EditorType) {
  const type = target ?? editorType.value
  if (!editorContainerRef.value) return

  const options: EditorOptions = {
    value: currentValue.value,
    language: 'markdown',
    theme: theme.value,
    placeholder: props.placeholder,
    readOnly: props.readOnly,
    minimap: props.minimap,
    wordWrap: props.wordWrap,
    fontSize: props.fontSize,
  }

  try {
    const adapter = await createEditor(type, editorContainerRef.value, options)
    editor.value = adapter

    adapter.onChange((value) => {
      currentValue.value = value
      emit('update:modelValue', value)
      emit('change', value)
    })

    adapter.focus()
  } catch (e) {
    message.error(`编辑器初始化失败: ${e instanceof Error ? e.message : e}`)
  }
}

function selectEditorType(t: EditorType) {
  if (t === editorType.value) return
  editorType.value = t
  emit('update:editorType', t)
  recreateEditor(t)
}

function toggleMode() {
  const next: EditorMode = mode.value === 'edit' ? 'render' : 'edit'
  mode.value = next
  emit('update:mode', next)
  emit('mode-change', next)
  nextTick(() => {
    if (next === 'edit' && !editor.value) {
      initEditor()
    }
    if (next === 'render' && renderer) {
      renderer.initialize()
    }
  })
}

function setLayout(l: LayoutMode) {
  layout.value = l
}

function toggleFullscreen() {
  if (!containerRef.value) return
  if (!isFullscreen.value) {
    if (containerRef.value.requestFullscreen) {
      containerRef.value.requestFullscreen().catch(() => {})
    }
    isFullscreen.value = true
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    }
    isFullscreen.value = false
  }
}

function startResize(e: MouseEvent) {
  e.preventDefault()

  const editorEl = document.querySelector('.markdown-editor__pane-editor') as HTMLElement | null
  const contentEl = document.querySelector('.markdown-editor__content') as HTMLElement | null
  if (!editorEl || !contentEl) return

  const startX = e.clientX
  const startWidth = editorEl.offsetWidth
  const contentWidth = contentEl.offsetWidth
  const minWidth = 150
  const maxWidth = contentWidth - 150

  function onMouseMove(ev: MouseEvent) {
    let newWidth = startWidth + (ev.clientX - startX)
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))
    editorEl!.style.flex = '0 0 auto'
    editorEl!.style.width = `${newWidth}px`
  }

  function onMouseUp() {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.body.style.cursor = ''
  }

  document.body.style.cursor = 'col-resize'
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

async function handleToolbarAction(action: string) {
  if (!editor.value || mode.value !== 'edit') return
  const ed = editor.value

  const actions: Record<string, () => void> = {
    heading1: () => ed.wrapLine('# ', ''),
    heading2: () => ed.wrapLine('## ', ''),
    heading3: () => ed.wrapLine('### ', ''),
    bold: () => ed.wrapSelection('**', '**'),
    italic: () => ed.wrapSelection('*', '*'),
    strikethrough: () => ed.wrapSelection('~~', '~~'),
    code: () => ed.wrapSelection('`', '`'),
    codeblock: () => ed.wrapSelection('\n```\n', '\n```\n'),
    'bullet-list': () => ed.toggleLinePrefix('- '),
    'ordered-list': () => ed.toggleLinePrefix('1. '),
    'task-list': () => ed.toggleLinePrefix('- [ ] '),
    indent: () => ed.indentLines(),
    outdent: () => ed.outdentLines(),
    link: () => ed.insertLink(),
    image: () => ed.insertImage(),
    table: () => ed.insertTable(),
    quote: () => ed.toggleLinePrefix('> '),
    hr: () => ed.insertHorizontalRule(),
    footnote: () => ed.insertFootnote(),
    'math-inline': () => ed.wrapSelection('$', '$'),
    'math-block': () => ed.wrapSelection('$$\n', '\n$$'),
    typst: () => ed.wrapSelection('\n```typst\n', '\n```\n'),
    undo: () => ed.undo(),
    redo: () => ed.redo(),
  }

  const fn = actions[action]
  if (fn) {
    fn()
    ed.focus()
  }
}

const divStyle = computed(() => ({
  height: props.height === '100%' || props.height === '100vh' ? '100%' : typeof props.height === 'number' ? `${props.height}px` : props.height,
}))

const showEditor = computed(() => mode.value === 'edit' && (layout.value === 'split' || layout.value === 'editor'))
const showPreview = computed(() => mode.value === 'edit' && layout.value === 'split')
const showRenderOnly = computed(() => mode.value === 'render')
const showPreviewOnly = computed(() => mode.value === 'edit' && layout.value === 'preview')
</script>

<template>
  <div
    ref="containerRef"
    class="markdown-editor"
    :data-theme="theme"
    :style="divStyle"
  >
    <div class="markdown-editor__topbar">
      <Toolbar
        v-if="mode === 'edit'"
        :editor="editor"
        @action="handleToolbarAction"
      />
      <div class="markdown-editor__topbar-right">
        <Dropdown :menu="{ items: editorComponentMenu, selectable: true, selectedKeys: [editorType], onClick: (e) => switchMode(e.key) }">
          <button class="markdown-editor__type-btn">
            <columns-2 :size="14" />
            <span>{{ editorTypeLabel }}</span>
            <chevron-down :size="14" />
          </button>
        </Dropdown>

        <button v-if="mode === 'edit'" class="markdown-editor__tool-btn" :title="layout === 'split' ? '仅编辑' : '分屏'" @click="setLayout(layout === 'split' ? 'editor' : 'split')">
          <panels-left-right :size="16" />
        </button>

        <button class="markdown-editor__tool-btn" :title="mode === 'edit' ? '渲染模式' : '编辑模式'" @click="toggleMode">
          <eye v-if="mode === 'edit'" :size="16" />
          <edit-3 v-else :size="16" />
        </button>

        <button class="markdown-editor__tool-btn" :title="isFullscreen ? '退出全屏' : '全屏'" @click="toggleFullscreen">
          <minimize-2 v-if="isFullscreen" :size="16" />
          <maximize-2 v-else :size="16" />
        </button>

        <button class="markdown-editor__tool-btn" :title="theme === 'dark' ? '亮色' : '暗色'" @click="toggleTheme">
          <moon v-if="theme === 'dark'" :size="16" />
          <sun v-else :size="16" />
        </button>
      </div>
    </div>

    <div class="markdown-editor__content">
      <template v-if="mode === 'edit'">
        <div v-if="showEditor" class="markdown-editor__pane markdown-editor__pane-editor">
          <div class="markdown-editor__pane-header">
            <span>Markdown</span>
            <span class="markdown-editor__pane-count">{{ wordCount }} 字符</span>
          </div>
          <div ref="editorContainerRef" class="markdown-editor__editor-container"></div>
        </div>

        <div
          v-if="showPreview || showPreviewOnly"
          class="markdown-editor__pane markdown-editor__pane-preview"
        >
          <div class="markdown-editor__pane-header">
            <span>预览</span>
          </div>
          <div ref="previewContainerRef" class="markdown-editor__preview-container">
            <Preview :renderer="markdownRenderer" :markdown="currentValue" />
          </div>
        </div>

        <div
          v-if="showEditor && showPreview"
          class="markdown-editor__resize"
          @mousedown="startResize"
        ></div>
      </template>

      <div v-else-if="showRenderOnly" class="markdown-editor__pane markdown-editor__pane-render">
        <div class="markdown-editor__pane-header">
          <span>文档</span>
        </div>
        <div class="markdown-editor__render-container">
          <Renderer :renderer="markdownRenderer" :markdown="currentValue" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.markdown-editor {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-bg);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.markdown-editor__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-secondary);
}

.markdown-editor__topbar-right {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-left: 1px solid var(--color-border);
}

.markdown-editor__type-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-bg);
  color: var(--color-text);
  font-size: 12px;
  cursor: pointer;
  transition: border-color 150ms ease;
}

.markdown-editor__type-btn:hover {
  border-color: var(--color-primary);
}

.markdown-editor__tool-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
}

.markdown-editor__tool-btn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text);
}

.markdown-editor__content {
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
  background: var(--color-bg);
}

.markdown-editor__pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.markdown-editor__pane-editor {
  flex: 1 1 auto;
  border-right: 1px solid var(--color-border);
}

.markdown-editor__pane-preview {
  flex: 1 1 auto;
}

.markdown-editor__pane-render {
  flex: 1;
}

.markdown-editor__pane-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-tertiary);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg);
}

.markdown-editor__pane-count {
  font-weight: 400;
}

.markdown-editor__editor-container,
.markdown-editor__preview-container,
.markdown-editor__render-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.markdown-editor__preview-container,
.markdown-editor__render-container {
  overflow: auto;
  background: var(--color-bg);
}

.markdown-editor__render-container {
  padding: 0;
}

.markdown-editor__resize {
  width: 6px;
  cursor: col-resize;
  position: relative;
  z-index: 5;
  margin: 0 -3px;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  transition: background 150ms ease;
}

.markdown-editor__resize:hover {
  background: var(--color-primary-hover);
}

@media (max-width: 768px) {
  .markdown-editor__content {
    flex-direction: column;
  }
  .markdown-editor__pane-editor {
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }
  .markdown-editor__resize {
    width: 100%;
    height: 6px;
    cursor: row-resize;
  }
}
</style>